import { service } from '@ember/service'
import Component from '@glimmer/component'
import type { IntlService } from 'ember-intl'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type { NormalizedInterval } from 'date-fns'
import type Student from 'teacher-ui-client/models/student'
import { StudentDashboardTile } from 'teacher-ui-client/components/tiles/student-dashboard'
import { t } from 'ember-intl'
import { TaskState } from 'teacher-ui-client/components/task-state'
import { StudentDashboardSubheader } from 'teacher-ui-client/components/tiles/student-dashboard/subheader'
import { ComprehensionProgressOverview } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/comprehension-progress/overview'
import type { Task } from 'ember-concurrency'
import type {
  AssessmentsEventsReportData,
  AssessmentsOverviewReportData,
  AssignmentsEventsReportData,
  AssignmentsOverviewReportData,
  AwardsOverviewReportData,
  BooksReadEventsReportData,
  BooksReadOverviewReportData,
  ComprehensionProgressEventsReportData,
  ComprehensionProgressOverviewReportData,
  LexileGrowthOverviewReportData,
  ProgressByCategoryOverviewReportData,
  SpellingProgressEventsReportData,
  SpellingProgressOverviewReportData,
  StadiumOverviewReportData,
} from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/student/index/route'
import { ComprehensionProgressPlacementEvents } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/comprehension-progress/placement-events'
import { ComprehensionProgressEvents } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/comprehension-progress/events'
import { array } from '@ember/helper'
import { ReportLoader } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/read-aloud-fluency/overview/report-loader'
import EventsReportLoader from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/read-aloud-fluency/events/report-loader'
import { StudentLexileGrowthOverview } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/lexile-growth/overview'
import { StudentProgressByCategoryOverview } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/progress-by-category/overview'
import { StudentAssessmentsOverview } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/assessments/overview'
import { StudentAssessmentsEvents } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/assessments/events'
import { DashboardStudentAssignmentsOverview } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/assignments/overview'
import { DashboardStudentAssignmentsEvents } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/assignments/events'
import { RexSpellingProgressOverview } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/spelling-progress/overview'
import { RexSpellingProgressEvents } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/spelling-progress/events'
import { StudentAwardsOverview } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/awards/overview'
import { StudentStadiumOverview } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/stadium/overview'
import { StudentBooksReadEvents } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/books-read/events'
import { StudentBooksReadOverview } from 'teacher-ui-client/components/reporting/reading-eggspress/dashboard/student/books-read/overview'

interface Args {
  period: NormalizedInterval
  student: Student
  fetchComprehensionProgressOverviewReportTask: Task<ComprehensionProgressOverviewReportData | undefined, unknown[]>
  fetchComprehensionProgressPlacementEventsReportTask: Task<
    ComprehensionProgressEventsReportData | undefined,
    unknown[]
  >
  fetchComprehensionProgressEventsReportTask: Task<ComprehensionProgressEventsReportData | undefined, unknown[]>
  fetchLexileGrowthOverviewReportTask: Task<LexileGrowthOverviewReportData | undefined, unknown[]>
  fetchProgressByCategoryOverviewReportTask: Task<ProgressByCategoryOverviewReportData | undefined, unknown[]>
  fetchAssessmentsOverviewReportTask: Task<AssessmentsOverviewReportData | undefined, unknown[]>
  fetchAssessmentsEventsReportTask: Task<AssessmentsEventsReportData | undefined, unknown[]>
  fetchAssignmentsOverviewReportTask: Task<AssignmentsOverviewReportData | undefined, unknown[]>
  fetchAssignmentsEventsReportTask: Task<AssignmentsEventsReportData | undefined, unknown[]>
  fetchSpellingProgressOverviewReportTask: Task<SpellingProgressOverviewReportData | undefined, unknown[]>
  fetchSpellingProgressEventsReportTask: Task<SpellingProgressEventsReportData | undefined, unknown[]>
  fetchAwardsOverviewReportTask: Task<AwardsOverviewReportData | undefined, unknown[]>
  fetchStadiumOverviewReportTask: Task<StadiumOverviewReportData | undefined, unknown[]>
  fetchBooksReadOverviewReportTask: Task<BooksReadOverviewReportData | undefined, unknown[]>
  fetchBooksReadEventsReportTask: Task<BooksReadEventsReportData | undefined, unknown[]>
}

interface Signature {
  Args: Args
}

export class ReadingEggspressDashboardStudentPage extends Component<Signature> {
  themeKey = ThemeKeys.blue

  @service intl!: IntlService

  get sectionAnchors() {
    return [
      this.intl.t('pages.reporting.readingEggspress.dashboard.student.comprehensionLessons.anchorTitle'),
      this.intl.t('pages.reporting.readingEggspress.dashboard.student.readAloudFluency.anchorTitle'),
      this.intl.t('pages.reporting.readingEggspress.dashboard.student.lexileGrowth.anchorTitle'),
      this.intl.t('pages.reporting.readingEggspress.dashboard.student.progressByCategory.anchorTitle'),
      this.intl.t('pages.reporting.readingEggspress.dashboard.student.assessments.anchorTitle'),
      this.intl.t('pages.reporting.readingEggspress.dashboard.student.assignments.anchorTitle'),
      this.intl.t('pages.reporting.readingEggspress.dashboard.student.spellingLessons.anchorTitle'),
      this.intl.t('pages.reporting.readingEggspress.dashboard.student.awards.anchorTitle'),
      this.intl.t('pages.reporting.readingEggspress.dashboard.student.stadium.anchorTitle'),
      this.intl.t('pages.reporting.readingEggspress.dashboard.student.booksRead.anchorTitle'),
    ]
  }

  <template>
    <div class="space-y-12">
      <div class="flex justify-center gap-4">
        <span class="whitespace-nowrap">{{t "pages.reporting.readingEggspress.dashboard.student.jumpTo"}}:</span>
        <div class="flex flex-wrap gap-x-8 gap-y-4">
          {{#each this.sectionAnchors as |sectionAnchor|}}
            <a class="font-semibold" href="#{{sectionAnchor}}">{{sectionAnchor}}</a>
          {{/each}}
        </div>
      </div>
      <StudentDashboardTile
        id={{t "pages.reporting.readingEggspress.dashboard.student.comprehensionLessons.anchorTitle"}}
        data-test-student-dashboard-tile="comprehension-lesson-progress"
        @theme={{this.themeKey}}
        @title={{t "pages.reporting.readingEggspress.dashboard.student.comprehensionLessons.title"}}
        @icon="phosphor-share-network-fill"
        @detailedStudentReportRoute="reporting.ui-scope.rex.dashboard.student.comprehension-lesson-progress"
      >
        <TaskState
          @task={{@fetchComprehensionProgressOverviewReportTask}}
          @initialLoadingClass="m-3"
          as |lastValue isRunning|
        >
          <ComprehensionProgressOverview
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.comprehensionLessons.title"}}
            @overviewData={{lastValue.overview}}
            @isLoading={{isRunning}}
          />
        </TaskState>
        <StudentDashboardSubheader>
          {{t "pages.reporting.readingEggspress.dashboard.student.placementTest"}}
        </StudentDashboardSubheader>
        <TaskState
          @task={{@fetchComprehensionProgressPlacementEventsReportTask}}
          @initialLoadingClass="m-3"
          as |lastValue isRunning|
        >
          <ComprehensionProgressPlacementEvents
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.placementTest"}}
            @eventsData={{lastValue.events}}
            @isLoading={{isRunning}}
          />
        </TaskState>
        <StudentDashboardSubheader>
          {{t "pages.reporting.readingEggspress.dashboard.student.recentActivity"}}
        </StudentDashboardSubheader>
        <TaskState
          @task={{@fetchComprehensionProgressEventsReportTask}}
          @initialLoadingClass="m-3"
          as |lastValue isRunning|
        >
          <ComprehensionProgressEvents
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.recentActivity"}}
            @eventsData={{lastValue.events}}
            @isLoading={{isRunning}}
          />
        </TaskState>
      </StudentDashboardTile>
      <StudentDashboardTile
        id={{t "pages.reporting.readingEggspress.dashboard.student.readAloudFluency.anchorTitle"}}
        data-test-student-dashboard-tile="read-aloud-fluency"
        @theme={{this.themeKey}}
        @title={{t "pages.reporting.readingEggspress.dashboard.student.readAloudFluency.title"}}
        @icon="phosphor-book-fill"
        @detailedStudentReportRoute="reporting.ui-scope.rex.read-aloud-fluency.student"
        @detailStudentReportModels={{if @student.id (array @student.id)}}
      >
        <ReportLoader @period={{@period}} @student={{@student}} />
        <StudentDashboardSubheader>
          {{t "pages.reporting.readingEggspress.dashboard.student.recentActivity"}}
        </StudentDashboardSubheader>
        <EventsReportLoader @period={{@period}} @student={{@student}} />
      </StudentDashboardTile>
      <StudentDashboardTile
        id={{t "pages.reporting.readingEggspress.dashboard.student.lexileGrowth.anchorTitle"}}
        data-test-student-dashboard-tile="lexile-growth"
        @theme={{this.themeKey}}
        @title={{t "pages.reporting.readingEggspress.dashboard.student.lexileGrowth.title"}}
        @icon="phosphor-chart-line-bold"
      >
        <TaskState @task={{@fetchLexileGrowthOverviewReportTask}} @initialLoadingClass="m-3" as |lastValue isRunning|>
          <StudentLexileGrowthOverview
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.lexileGrowth.title"}}
            @overviewData={{lastValue.overview}}
            @isLoading={{isRunning}}
          />
        </TaskState>
      </StudentDashboardTile>
      <StudentDashboardTile
        id={{t "pages.reporting.readingEggspress.dashboard.student.progressByCategory.anchorTitle"}}
        data-test-student-dashboard-tile="progress-by-category"
        @theme={{this.themeKey}}
        @title={{t "pages.reporting.readingEggspress.dashboard.student.progressByCategory.title"}}
        @icon="phosphor-chart-line-bold"
      >
        <TaskState
          @task={{@fetchProgressByCategoryOverviewReportTask}}
          @initialLoadingClass="m-3"
          as |lastValue isRunning|
        >
          <StudentProgressByCategoryOverview
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.progressByCategory.title"}}
            @overviewData={{lastValue.overview}}
            @isLoading={{isRunning}}
          />
        </TaskState>
      </StudentDashboardTile>
      <StudentDashboardTile
        id={{t "pages.reporting.readingEggspress.dashboard.student.assessments.anchorTitle"}}
        data-test-student-dashboard-tile="assessments"
        @theme={{this.themeKey}}
        @title={{t "pages.reporting.readingEggspress.dashboard.student.assessments.title"}}
        @icon="phosphor-share-network-fill"
        @detailedStudentReportRoute="reporting.ui-scope.rex.dashboard.student.assessments"
      >
        <TaskState @task={{@fetchAssessmentsOverviewReportTask}} @initialLoadingClass="m-3" as |lastValue isRunning|>
          <StudentAssessmentsOverview
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.assessments.title"}}
            @overviewData={{lastValue.overview}}
            @isLoading={{isRunning}}
          />
        </TaskState>
        <StudentDashboardSubheader>
          {{t "pages.reporting.readingEggspress.dashboard.student.recentActivity"}}
        </StudentDashboardSubheader>
        <TaskState @task={{@fetchAssessmentsEventsReportTask}} @initialLoadingClass="m-3" as |lastValue isRunning|>
          <StudentAssessmentsEvents
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.recentActivity"}}
            @eventsData={{lastValue.events}}
            @isLoading={{isRunning}}
          />
        </TaskState>
      </StudentDashboardTile>
      <StudentDashboardTile
        id={{t "pages.reporting.readingEggspress.dashboard.student.assignments.anchorTitle"}}
        data-test-student-dashboard-tile="assignments"
        @theme={{this.themeKey}}
        @title={{t "pages.reporting.readingEggspress.dashboard.student.assignments.title"}}
        @icon="phosphor-book-fill"
        @detailedStudentReportRoute="reporting.ui-scope.rex.dashboard.student.assignments"
      >
        <TaskState @task={{@fetchAssignmentsOverviewReportTask}} @initialLoadingClass="m-3" as |lastValue isRunning|>
          <DashboardStudentAssignmentsOverview
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.assignments.title"}}
            @overviewData={{lastValue.overview}}
            @isLoading={{isRunning}}
          />
        </TaskState>
        <StudentDashboardSubheader>
          {{t "pages.reporting.readingEggspress.dashboard.student.recentActivity"}}
        </StudentDashboardSubheader>
        <TaskState @task={{@fetchAssignmentsEventsReportTask}} @initialLoadingClass="m-3" as |lastValue isRunning|>
          <DashboardStudentAssignmentsEvents
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.recentActivity"}}
            @eventsData={{lastValue.events}}
            @isLoading={{isRunning}}
          />
        </TaskState>
      </StudentDashboardTile>
      <StudentDashboardTile
        id={{t "pages.reporting.readingEggspress.dashboard.student.spellingLessons.anchorTitle"}}
        data-test-student-dashboard-tile="spelling-lesson-progress"
        @theme={{this.themeKey}}
        @title={{t "pages.reporting.readingEggspress.dashboard.student.spellingLessons.title"}}
        @icon="phosphor-shooting-star-fill"
        @detailedStudentReportRoute="reporting.ui-scope.rex.dashboard.student.spelling-lesson-progress"
      >
        <TaskState
          @task={{@fetchSpellingProgressOverviewReportTask}}
          @initialLoadingClass="m-3"
          as |lastValue isRunning|
        >
          <RexSpellingProgressOverview
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.spellingLessons.title"}}
            @overviewData={{lastValue.overview}}
            @isLoading={{isRunning}}
          />
        </TaskState>
        <StudentDashboardSubheader>
          {{t "pages.reporting.readingEggspress.dashboard.student.recentActivity"}}
        </StudentDashboardSubheader>
        <TaskState @task={{@fetchSpellingProgressEventsReportTask}} @initialLoadingClass="m-3" as |lastValue isRunning|>
          <RexSpellingProgressEvents
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.recentActivity"}}
            @eventsData={{lastValue.events}}
            @isLoading={{isRunning}}
          />
        </TaskState>
      </StudentDashboardTile>
      <StudentDashboardTile
        id={{t "pages.reporting.readingEggspress.dashboard.student.awards.anchorTitle"}}
        data-test-student-dashboard-tile="awards"
        @theme={{this.themeKey}}
        @title={{t "pages.reporting.readingEggspress.dashboard.student.awards.title"}}
        @icon="phosphor-trophy-fill"
      >
        <TaskState @task={{@fetchAwardsOverviewReportTask}} @initialLoadingClass="m-3" as |lastValue isRunning|>
          <StudentAwardsOverview
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.awards.title"}}
            @overviewData={{lastValue.overview}}
            @isLoading={{isRunning}}
          />
        </TaskState>
      </StudentDashboardTile>
      <StudentDashboardTile
        id={{t "pages.reporting.readingEggspress.dashboard.student.stadium.anchorTitle"}}
        data-test-student-dashboard-tile="stadium"
        @theme={{this.themeKey}}
        @title={{t "pages.reporting.readingEggspress.dashboard.student.stadium.title"}}
        @icon="phosphor-bank-fill"
      >
        <TaskState @task={{@fetchStadiumOverviewReportTask}} @initialLoadingClass="m-3" as |lastValue isRunning|>
          <StudentStadiumOverview
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.stadium.title"}}
            @overviewData={{lastValue.overview}}
            @isLoading={{isRunning}}
          />
        </TaskState>
      </StudentDashboardTile>
      <StudentDashboardTile
        id={{t "pages.reporting.readingEggspress.dashboard.student.booksRead.anchorTitle"}}
        data-test-student-dashboard-tile="books-read"
        @theme={{this.themeKey}}
        @title={{t "pages.reporting.readingEggspress.dashboard.student.booksRead.title"}}
        @icon="phosphor-shooting-star-fill"
        @detailedStudentReportRoute="reporting.ui-scope.rex.dashboard.student.books-read"
      >
        <TaskState @task={{@fetchBooksReadOverviewReportTask}} @initialLoadingClass="m-3" as |lastValue isRunning|>
          <StudentBooksReadOverview
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.booksRead.title"}}
            @overviewData={{lastValue.overview}}
            @isLoading={{isRunning}}
          />
        </TaskState>
        <StudentDashboardSubheader>
          {{t "pages.reporting.readingEggspress.dashboard.student.recentActivity"}}
        </StudentDashboardSubheader>
        <TaskState @task={{@fetchBooksReadEventsReportTask}} @initialLoadingClass="m-3" as |lastValue isRunning|>
          <StudentBooksReadEvents
            class="w-full"
            exportable={{t "pages.reporting.readingEggspress.dashboard.student.recentActivity"}}
            @eventsData={{lastValue.events}}
            @isLoading={{isRunning}}
          />
        </TaskState>
      </StudentDashboardTile>
    </div>
  </template>
}

export default ReadingEggspressDashboardStudentPage
